import React, { useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useAdditionalInfos } from '../hooks/use-additional-infos/additional-infos-provider';
import styles from './create-narrative-form.module.css';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { OnLexicalError } from '../../common/components/lexical/types';
import { EditorControl } from '../../common/components/lexical';
import { useShowErrorAlert } from '../hooks/use-show-error-alert/use-show-error-alert';
import { v4 as uuidv4 } from 'uuid';
import { AxiosError } from 'axios-cad-nx';
import { ApiResult } from '@mark43/cad-api';
import { NarrativeInput, NarrativeInputFieldValues } from './narrative-input';
import { CrossClassInformation } from '../types';

/* eslint-disable-next-line */
export interface CreateNarrativeFormProps {
    editorControlRef: React.MutableRefObject<EditorControl | undefined>;
    crossClassInformation?: CrossClassInformation;
    isFirstResponder?: boolean;
    onLexicalError?: OnLexicalError;
    onReplaceVehicles?: (vehicles: string[], narrativeId: string) => void;
}

export function CreateNarrativeForm({
    editorControlRef,
    crossClassInformation,
    onLexicalError,
    onReplaceVehicles,
    isFirstResponder,
}: CreateNarrativeFormProps): React.JSX.Element {
    const {
        entityId,
        entityNumber,
        createAdditionalInfo,
        isHighPriority,
        setIsHighPriority,
        editorState,
        storeEditorState,
    } = useAdditionalInfos();
    const { handleSubmit, control, resetField } = useForm<NarrativeInputFieldValues>();
    const { showFailedToSendAlert } = useShowErrorAlert();
    const shouldResetPriority =
        useEnabledFeatures()?.['narrativeSelectionsResetAfterEachEntry'] ?? true;
    const narrativeId = useRef<string>(uuidv4());
    const defaultCrossClassOptionsValues = crossClassInformation?.crossClassOptions.map(
        (option) => option.value
    );

    const onSubmit: SubmitHandler<NarrativeInputFieldValues> = async (data) => {
        if (data['narrative-input'] === '') {
            return;
        }

        try {
            await createAdditionalInfo({
                agencyEventId: entityId,
                isHighPriority,
                text: data['narrative-input'],
                crossClassOptions: data['cross-class-options'] ?? defaultCrossClassOptionsValues,
                toExternalCadIntegrations: [],
            });
            resetField('narrative-input');
            editorControlRef.current?.clear();
            if (shouldResetPriority) {
                setIsHighPriority(false);
            }
            narrativeId.current = uuidv4();
        } catch (error) {
            editorControlRef.current?.focus();
            showFailedToSendAlert(
                entityNumber,
                data['narrative-input'],
                (error as AxiosError<ApiResult>)?.response?.data?.error
            );
        } finally {
            editorControlRef.current?.setEditable(true);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className={styles['form']}>
            <NarrativeInput
                agencyEventId={entityId}
                data-testid="NARRATIVE_INPUT"
                editorControlRef={editorControlRef}
                isHighPriority={isHighPriority}
                setIsHighPriority={setIsHighPriority}
                onSubmit={handleSubmit(onSubmit)}
                control={control}
                onLexicalError={onLexicalError}
                onReplaceVehicles={(vehicles: string[]) => {
                    onReplaceVehicles?.(vehicles, narrativeId.current);
                }}
                crossClassInformation={crossClassInformation}
                editorState={editorState}
                storeEditorState={storeEditorState}
                isFirstResponder={isFirstResponder}
            />
            <input type="submit" hidden />
        </form>
    );
}

export default CreateNarrativeForm;
